import { PayoutHybridSourceFields } from './payout-agreements.types';
import { AdminRole } from './user.types';

export type PayoutCadence = 'DAILY' | 'WEEKLY' | 'MONTHLY';
export type PayoutFeeBearer = 'PLATFORM' | 'RECIPIENT';

export interface ConfigurePayoutRunInput {
  cadence: PayoutCadence;
  cutoffLocalTime: string;
  timeZone: string;
  provider: string;
  currency: string;
  feeBearer: PayoutFeeBearer;
  recipientChargeAmount: string;
  approverUserIds: number[];
  executorUserIds: number[];
  automaticStatements?: boolean;
  weeklyClosingDay?: number;
  partnerChargeAllocation?: 'EQUAL';
  activate: boolean;
}

export interface PayoutRunConfiguration extends ConfigurePayoutRunInput {
  partnerRepaymentAuthorization?: 'ALL_PARTNER_AGREEMENTS';
  id: string;
  version: number;
  active: boolean;
}

export interface PayoutRunInput {
  expectedReviewHash?: string;
  obligationId?: string;
  serviceDayIds?: string[];
  riderId: number;
  periodStart: string;
  periodEnd: string;
  bonuses: Array<{ reference: string; amount: string }>;
}

export interface PayoutRepaymentAttribution {
  repaymentCapPercentage?: string | null;
  grossAmount?: string;
  commercialFeeAmount?: string;
  netContributionAmount?: string | null;
  partnerRepaymentAuthorization?: 'ALL_PARTNER_AGREEMENTS';
}
export interface PayoutRepaymentAllocation {
  debtId: number;
  amount: string;
  state?: string;
  reservedAmount?: string;
  collectedAmount?: string;
  releasedAmount?: string;
  remainingAmount?: string;
}
export interface PayoutRunPreview extends PayoutRepaymentAttribution {
  noEarnings?: boolean;
  reviewHash?: string;
  configuration?: PayoutRunConfiguration;
  canFreeze?: boolean;
  paymentStatus?: string;
  repaymentAmount?: string;
  repayments?: PayoutRepaymentAllocation[];
  settlementKind?: 'BANK' | 'DEBT_ONLY';
  currentStatementId?: string;
  calculation: {
    recipient?: {
      mode: 'RIDER' | 'PARTNER';
      riderId?: number;
      partnerId?: number;
    } | null;
    sources?: PayoutStatementRevision['sources'];
    grossAmount: string;
    netAmount: string | null;
    components: Array<{ type: string; label: string; amount: string }>;
  };
  approvedAmount: string | null;
  currency: string;
  provider: string;
  feeBearer: PayoutFeeBearer;
  recipientChargeAmount: string;
  destination: {
    accountName: string;
    accountNumber: string;
    bankName: string;
  } | null;
  blockingExceptions: Array<{ code: string; message: string }>;
}

export interface PayoutDeliveryOrderDetails {
  provenance: 'STATEMENT_CAPTURE' | 'CURRENT_ORDER';
  orderSlug: string;
  destination: { address: string; area: string } | null;
  distance: {
    value: string;
    kind: 'ACTUAL' | 'ESTIMATED';
    unit: 'METRES' | 'AS_RECORDED';
  } | null;
  customerDeliveryFee: string | null;
  customerOrderTotal: string | null;
  paymentStatus: 'SIMULATED' | 'MARKED_PAID' | 'UNPAID';
}

export interface PayoutStatementRevision extends PayoutRepaymentAttribution {
  frozenAt?: string | null;
  initiatedAt?: string | null;
  paymentInitiatedAt?: string | null;
  updatedAt?: string | null;
  riderId?: number;
  disbursementId?: string | null;
  constituentCount?: number | null;
  disbursementAmount?: string | null;
  aggregateApprovedAmount?: string | null;
  aggregateRepaymentAmount?: string | null;
  paymentDescription?: string;
  id: string;
  configurationId: string;
  revisionNumber: number;
  periodStart: string;
  periodEnd: string;
  recipient: {
    mode: 'RIDER' | 'PARTNER';
    riderId?: number;
    partnerId?: number;
  };
  state: 'HELD' | 'FROZEN' | 'APPROVED' | 'STALE' | 'CANCELLED';
  constituents?: PayoutStatementRevision[];
  obligationId?: string;
  earnedNetAmount?: string;
  paymentStatus?: string;
  repaymentAmount?: string;
  repayments?: PayoutRepaymentAllocation[];
  settlementKind?: 'BANK' | 'DEBT_ONLY';
  periodLocalStart?: string;
  periodLocalEnd?: string;
  readinessExceptions?: Array<{ code: string; message: string }>;
  sources?: Array<
    PayoutHybridSourceFields & {
      type: string;
      sourceId?: number | string;
      deliveryDetails?: PayoutDeliveryOrderDetails;
      tip?: string;
      correctionId?: string;
      originalStatementId?: string;
      originalSourceKeys?: string[];
      reason?: string;
      amount?: string;
      amountBasis?: string;
      ownershipType?: string;
      attributedPartnerId?: number | null;
      riderAttributionId?: string | number;
      agreementAssignmentId?: string;
      agreementRevisionId?: string;
      serviceDayId?: string;
      serviceDate?: string;
      evidenceReference?: string;
      satisfiedConditions?: string[];
      approvedByUserId?: number | null;
      dailyEarnings?: string;
      dailyTimezone?: string;
      dailyCutoff?: string;
      earningMethod?: 'STANDARD' | 'FIXED_PER_DELIVERY' | 'DAILY' | 'HYBRID';
      fixedDeliveryAmount?: string | null;
      deliveryEarnings?: string;
      storedRiderCharge?: string;
      agreementVersion?: number;
      earnedAt?: string;
      bonusKind?: string;
      bonusGrantId?: string;
      reference?: string;
      frequency?: PayoutCadence;
      historicalAttributionStatus?: string;
      historicalSources?: Array<{
        type: string;
        sourceId?: string | number;
        reference?: string;
        bonusGrantId?: string | null;
        bonusKind?: string;
        frequency?: string;
        periodStart?: string;
        periodEnd?: string;
      }>;
      periodStart?: string;
      periodEnd?: string;
    }
  >;
  approvedAmount: string;
  currency: string;
  provider: string;
  feeBearer: PayoutFeeBearer;
  recipientChargeAmount: string;
  components: Array<{ type: string; label: string; amount: string }>;
  destination: {
    accountName: string | null;
    accountNumber: string | null;
    bankName: string | null;
  };
  approvalEvents: Array<{
    eventType: string;
    createdAt: string;
    actorUserId: number;
    reason?: string | null;
  }>;
  execution?: {
    state: string;
    provider: string;
    reference: string;
    amount: string;
    currency: string;
    destination: { accountNumber: string | null; bankName: string | null };
    providerCharge: {
      amount: string;
      currency: string;
      feeBearer: PayoutFeeBearer;
      recordedAt: string;
    } | null;
  } | null;
}

export const payoutRunRoles: AdminRole[] = ['OWNER', 'OPERATIONS', 'FINANCE'];

export interface GrantPayoutBonusInput {
  riderId: number;
  bonusKind: string;
  frequency: PayoutCadence;
  periodStart: string;
  periodEnd: string;
  amount: string;
}

export interface PartnerPayoutRunInput {
  chargeAllocationMode?: 'EQUAL';
  expectedReviewHash?: string;
  riderIds: number[];
  obligationIds?: Array<{ riderId: number; obligationId: string }>;
  periodStart: string;
  periodEnd: string;
  bonuses: Array<{ reference: string; amount: string }>;
  chargeAllocations?: Array<{ riderId: number; amount: string }>;
}
export interface PartnerPayoutPreview extends PayoutRepaymentAttribution {
  reviewHash?: string;
  repaymentAmount?: string;
  settlementKind?: 'BANK' | 'DEBT_ONLY';
  constituents: Array<PayoutRunPreview & { riderId: number }>;
  grossAmount: string;
  commercialFeeAmount: string;
  recipientChargeAmount: string;
  approvedAmount: string | null;
  blockingExceptions: Array<{ code: string; message: string }>;
  canFreeze: boolean;
  recipient: { mode: 'PARTNER'; partnerId: number } | null;
  currency: string;
  provider: string;
}
export interface PartnerPayoutAggregate extends PayoutRepaymentAttribution {
  repaymentAmount?: string;
  id: string;
  statements: PayoutStatementRevision[];
  approvedAmount: string;
  grossAmount: string;
  commercialFeeAmount: string;
  recipientChargeAmount: string;
  currency: string;
  provider: string;
  state: string;
}

export interface PayoutStatementScheduleRun {
  id: string;
  configurationId: string;
  periodStart: string;
  periodEnd: string;
  createdAt: string;
  results: Array<{
    outcome: 'EXISTING' | 'BLOCKED' | 'HELD' | 'FROZEN';
    riderId?: number;
    partnerId?: number;
    riderIds?: number[];
    disbursementId?: string;
    statementIds?: string[];
    blockers?: Array<{ code: string; message: string }>;
  }>;
}
